import axios from "axios";

/**
 * Safe getters: work whether token is stored as plain string or JSON stringified.
 */
function getLocalToken(key) {
    const v = window.localStorage.getItem(key);
    if (!v) return null;
    try {
        const parsed = JSON.parse(v);
        // support both "tokenString" and { token: "tokenString" }
        if (typeof parsed === "string") return parsed;
        return parsed?.token ?? null;
    } catch {
        return v;
    }
}

function setLocalToken(key, value) {
    // store consistently as JSON stringified string
    window.localStorage.setItem(key, JSON.stringify(value));
}

function clearAuthStorage() {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");
}

/**
 * Axios instance
 */
export const client = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

console.log('🔗 API baseURL:', process.env.REACT_APP_API_URL);

/**
 * Refresh call (uses the same client but guarded in interceptor to avoid loops)
 */
async function refreshTokenRequest() {
    const refreshToken = getLocalToken("refreshToken");

    // If there's no refresh token — fail fast
    if (!refreshToken) {
        const err = new Error("No refresh token");
        err._noRefreshToken = true;
        throw err;
    }

    return client.post("/refresh-token", { refreshToken });
}

/**
 * Prevent multiple parallel refresh calls (optional but recommended)
 */
let refreshPromise = null;

export const request = () => {
    // --- Request interceptor (attach access token) ---
    client.interceptors.request.use(
        (config) => {
            const accessToken = getLocalToken("accessToken");
            if (accessToken) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
            console.log('📤 Request:', config.method?.toUpperCase(), config.baseURL + config.url);
            return config;
        },
        (error) => Promise.reject(error)
    );

    // --- Response interceptor (handle 401 + refresh) ---
    client.interceptors.response.use(
        (response) => {
            console.log('✅ Response:', response.status, response.config.url);
            return response;
        },
        async (error) => {
            const originalConfig = error.config;

            console.error('❌ Error:', {
                status: error.response?.status,
                url: originalConfig?.url,
                method: originalConfig?.method?.toUpperCase(),
                message: error.message,
                data: error.response?.data
            });

            // Network/CORS error -> no response at all
            if (!error.response) {
                return Promise.reject(error);
            }

            // Do not refresh on the refresh endpoint itself (avoid infinite loop)
            if (originalConfig?.url?.includes("/refresh-token")) {
                return Promise.reject(error);
            }

            if (error.response.status === 401 && !originalConfig._retry) {
                originalConfig._retry = true;
                try {
                    // If a refresh is already running, await it; else start one
                    if (!refreshPromise) {
                        refreshPromise = refreshTokenRequest()
                            .then((rs) => rs)
                            .finally(() => {
                                refreshPromise = null;
                            });
                    }
                    const rs = await refreshPromise;
                    const newAccessToken = rs.data?.accessToken;
                    const newRefreshToken = rs.data?.refreshToken;
                    if (!newAccessToken || !newRefreshToken) {
                        clearAuthStorage();
                        return Promise.reject({ message: "Refresh failed: tokens missing" });
                    }
                    setLocalToken("accessToken", newAccessToken);
                    setLocalToken("refreshToken", newRefreshToken);

                    // Update default header + retry original request
                    client.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
                    originalConfig.headers = originalConfig.headers || {};
                    originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;

                    return client(originalConfig);
                } catch (e) {
                    // If refresh failed (expired/invalid) => clear storage
                    const msg =
                        e?.response?.data?.error?.message ||
                        e?.response?.data?.message ||
                        e?.message;

                    if (
                        msg === "Invalid token" ||
                        String(msg).toLowerCase().includes("expired") ||
                        e?._noRefreshToken
                    ) {
                        clearAuthStorage();
                    }

                    return Promise.reject(e?.response?.data || e);
                }
            }

            // For other statuses, just reject with server payload if exists
            if (error.response.data) {
                return Promise.reject(error.response.data);
            }

            return Promise.reject(error);
        }
    );

    return client;
};

export const axiosReq = request();

/**
 * Optional helpers you can export if needed
 */
export function getLocalAccessToken() {
    return getLocalToken("accessToken");
}
export function getLocalRefreshToken() {
    return getLocalToken("refreshToken");
}
export function setTokens({ accessToken, refreshToken }) {
    if (accessToken) setLocalToken("accessToken", accessToken);
    if (refreshToken) setLocalToken("refreshToken", refreshToken);
}
export function logout() {
    clearAuthStorage();
}
